import type Feature from 'ol/Feature';
import type { SimpleGeometry } from 'ol/geom';
import { KML_FEATURE_TYPE, KmlFeatureType } from '../../../featureTypes';
import { JobType } from '../../../types/types';
import { MapType } from './mapTypes';
import { getFeatureType, isScheduleOverlay } from './features';

/**
 * Feature types that can never be dragged via the Modify interaction
 * on a given map type. Mirrors the NON_DRAGGABLE_FEATURES_BY_MAP table
 * originally in KmlLayer.
 *
 * In sampling mode, sample zones are locked (the robot relies on them
 * being stable), and centroids / core points are positioned by the
 * sample-site model rather than by user drag.
 */
export const NON_DRAGGABLE_FEATURES_BY_MAP: Record<MapType, KmlFeatureType[]> = {
  sampling: [KML_FEATURE_TYPE.SAMPLE_ZONE, KML_FEATURE_TYPE.CENTROID, KML_FEATURE_TYPE.CORE_POINT],
  maps: [],
  all: [],
};

/**
 * Feature types that can never be dragged via the Modify interaction
 * on a given job type. Mirrors NON_DRAGGABLE_FEATURES_BY_JOB from the
 * original KmlLayer.
 */
export const NON_DRAGGABLE_FEATURES_BY_JOB: Record<JobType, KmlFeatureType[]> = {
  Grid: [KML_FEATURE_TYPE.CORE_POINT],
  Zone: [],
  Modgrid: [],
};

/**
 * Filter a list of selected features down to the ones that can be
 * dragged in the given map/job context. Used by KmlLayer (on click)
 * and ZonesLayer (on POLY_SELECTED) to keep
 * `selectionManager.draggableFeatures` in sync with the current
 * selection's drag eligibility.
 *
 * Reference overlays (the MapMaker scheduling layer) are never
 * draggable: they show another job's geometry, so dragging a vertex
 * would either do nothing or edit the wrong mission.
 */
export function filterDraggable(
  features: Feature<SimpleGeometry>[],
  mapType: MapType,
  jobType: JobType,
): Feature<SimpleGeometry>[] {
  const nonDraggable = [...NON_DRAGGABLE_FEATURES_BY_MAP[mapType], ...NON_DRAGGABLE_FEATURES_BY_JOB[jobType]];

  return features.filter((feature) => {
    const properties = feature.getProperties();

    if (isScheduleOverlay(properties)) {
      return false;
    }

    const type = getFeatureType(properties);

    return type ? !nonDraggable.includes(type) : true;
  });
}

// ---------------------------------------------------------------------
// Sidebar action eligibility — which feature types support which
// sidebar action (delete / change zone type / set waypoint / skip /
// move point) under which job type. Drives the candidate callbacks
// fired by KmlLayer.handleSelectFeatures.
// ---------------------------------------------------------------------

export const DELETEABLE_FEATURES_BY_JOB: Record<JobType, KmlFeatureType[]> = {
  Grid: [
    KML_FEATURE_TYPE.FIELD,
    KML_FEATURE_TYPE.CENTROID,
    KML_FEATURE_TYPE.GENERIC_ZONE,
    KML_FEATURE_TYPE.SLOW,
    KML_FEATURE_TYPE.UNSAFE,
    KML_FEATURE_TYPE.COLLECTED_FIELD,
    KML_FEATURE_TYPE.SAMPLE_ZONE,

    KML_FEATURE_TYPE.PARKING_POINT,
    KML_FEATURE_TYPE.STARTING_POINT,
    KML_FEATURE_TYPE.END_POINT,
  ],
  Zone: [
    KML_FEATURE_TYPE.FIELD,
    KML_FEATURE_TYPE.CENTROID,
    KML_FEATURE_TYPE.GENERIC_ZONE,
    KML_FEATURE_TYPE.SLOW,
    KML_FEATURE_TYPE.UNSAFE,
    KML_FEATURE_TYPE.COLLECTED_FIELD,
    KML_FEATURE_TYPE.SAMPLE_ZONE,
    KML_FEATURE_TYPE.CORE_POINT,

    KML_FEATURE_TYPE.PARKING_POINT,
    KML_FEATURE_TYPE.STARTING_POINT,
    KML_FEATURE_TYPE.END_POINT,
  ],
  Modgrid: [
    KML_FEATURE_TYPE.CENTROID,
    KML_FEATURE_TYPE.GENERIC_ZONE,
    KML_FEATURE_TYPE.SLOW,
    KML_FEATURE_TYPE.UNSAFE,
    KML_FEATURE_TYPE.COLLECTED_FIELD,
    KML_FEATURE_TYPE.SAMPLE_ZONE,

    KML_FEATURE_TYPE.PARKING_POINT,
    KML_FEATURE_TYPE.STARTING_POINT,
    KML_FEATURE_TYPE.END_POINT,
  ],
};

export const ZONE_TYPE_CHANGEABLE_FEATURES_BY_JOB: Record<JobType, KmlFeatureType[]> = {
  Grid: [
    KML_FEATURE_TYPE.FIELD,
    KML_FEATURE_TYPE.GENERIC_ZONE,
    KML_FEATURE_TYPE.SLOW,
    KML_FEATURE_TYPE.UNSAFE,
    KML_FEATURE_TYPE.COLLECTED_FIELD,
  ],
  Zone: [
    KML_FEATURE_TYPE.FIELD,
    KML_FEATURE_TYPE.GENERIC_ZONE,
    KML_FEATURE_TYPE.SLOW,
    KML_FEATURE_TYPE.UNSAFE,
    KML_FEATURE_TYPE.COLLECTED_FIELD,
  ],
  Modgrid: [
    KML_FEATURE_TYPE.FIELD,
    KML_FEATURE_TYPE.GENERIC_ZONE,
    KML_FEATURE_TYPE.SLOW,
    KML_FEATURE_TYPE.UNSAFE,
    KML_FEATURE_TYPE.COLLECTED_FIELD,
  ],
};

export const WAYPOINT_SETTABLE_FEATURES_BY_JOB: Record<JobType, KmlFeatureType[]> = {
  Grid: [KML_FEATURE_TYPE.CENTROID],
  Zone: [KML_FEATURE_TYPE.SAMPLE_ZONE, KML_FEATURE_TYPE.CORE_POINT],
  Modgrid: [KML_FEATURE_TYPE.CENTROID],
};

export const SKIP_SETTABLE_FEATURES_BY_JOB: Record<JobType, KmlFeatureType[]> = {
  Grid: [KML_FEATURE_TYPE.CENTROID],
  Zone: [KML_FEATURE_TYPE.SAMPLE_ZONE, KML_FEATURE_TYPE.CORE_POINT],
  Modgrid: [KML_FEATURE_TYPE.CENTROID],
};

export const POINT_MOVABLE_FEATURES_BY_JOB: Record<JobType, KmlFeatureType[]> = {
  Grid: [KML_FEATURE_TYPE.CENTROID],
  Zone: [KML_FEATURE_TYPE.CORE_POINT],
  Modgrid: [KML_FEATURE_TYPE.CENTROID],
};

// ---------------------------------------------------------------------
// Selection priority — when the user clicks a spot that hits multiple
// overlapping features, pick the one with the highest priority. The
// numbers are bucketed by feature category (zones low, paths middle,
// points high, overlays top). Used by KmlLayer.handleSelectFeatures.
// ---------------------------------------------------------------------

const PRIORITY_LAST = -1;
const PRIORITY_ZONE = 0;
const PRIORITY_POLY = 0;
const PRIORITY_FIELD = 1;
const PRIORITY_PATH = 1;
const PRIORITY_COLLECTED_FIELD = 2;
const PRIORITY_STATIC_POINT = 3;
const PRIORITY_CORE = 3;
const PRIORITY_CENTROID = 4;

export const SELECTION_PRIORITIES: Record<KmlFeatureType, number> = {
  [KML_FEATURE_TYPE.GENERIC_ZONE]: PRIORITY_ZONE,
  [KML_FEATURE_TYPE.SAMPLE_ZONE]: PRIORITY_ZONE,
  [KML_FEATURE_TYPE.SLOW]: PRIORITY_ZONE,
  [KML_FEATURE_TYPE.UNSAFE]: PRIORITY_ZONE,
  [KML_FEATURE_TYPE.VISUAL_ZONE]: PRIORITY_ZONE,
  [KML_FEATURE_TYPE.LASSO_ZONE]: PRIORITY_ZONE,

  [KML_FEATURE_TYPE.PATH]: PRIORITY_PATH,
  [KML_FEATURE_TYPE.FIELD]: PRIORITY_FIELD,
  [KML_FEATURE_TYPE.COLLECTED_FIELD]: PRIORITY_COLLECTED_FIELD,
  [KML_FEATURE_TYPE.CORE_POINT]: PRIORITY_CORE,
  [KML_FEATURE_TYPE.PULLIN]: PRIORITY_STATIC_POINT,
  [KML_FEATURE_TYPE.PARKING_POINT]: PRIORITY_STATIC_POINT,
  [KML_FEATURE_TYPE.STARTING_POINT]: PRIORITY_STATIC_POINT,
  [KML_FEATURE_TYPE.END_POINT]: PRIORITY_STATIC_POINT,
  [KML_FEATURE_TYPE.CENTROID]: PRIORITY_CENTROID,
  [KML_FEATURE_TYPE.POLY]: PRIORITY_POLY,

  [KML_FEATURE_TYPE.REORDER_LINE]: PRIORITY_LAST,
  [KML_FEATURE_TYPE.CORE_LINE]: PRIORITY_LAST,
  [KML_FEATURE_TYPE.DUMP_EVENT]: PRIORITY_LAST,
  [KML_FEATURE_TYPE.ERASER]: PRIORITY_LAST,
  [KML_FEATURE_TYPE.CUTTER]: PRIORITY_LAST,
};
